import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Modal,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Popconfirm,
  Tooltip,
  Form,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ExpenseTransactionModal } from '../../../components/expenses/ExpenseTransactionModal';
import {
  ExpenseTransaction,
  ExpenseCategory,
  getExpenseTransactions,
  getExpenseCategories,
  deleteExpenseTransaction,
  formatCurrency,
} from '../../../services/expenseService';

const { RangePicker } = DatePicker;

export const ExpensesPage: React.FC = () => {
  const [transactions, setTransactions] = useState<ExpenseTransaction[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<ExpenseTransaction | undefined>();
  const [filters, setFilters] = useState({
    category_id: undefined,
    date_range: undefined,
    created_by: undefined,
    search: '',
  });

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const response = await getExpenseTransactions({
        page: currentPage,
        limit: pageSize,
        ...filters,
      });
      setTransactions(response.data);
      setTotal(response.pagination.total);
    } catch (error) {
      message.error('Không thể tải danh sách giao dịch');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await getExpenseCategories();
      setCategories(categoriesData);
    } catch (error) {
      message.error('Không thể tải danh mục');
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    loadCategories();
  }, []);

  const handleAddTransaction = () => {
    setEditingTransaction(undefined);
    setIsModalOpen(true);
  };

  const handleEditTransaction = (record: ExpenseTransaction) => {
    setEditingTransaction(record);
    setIsModalOpen(true);
  };

  const handleDeleteTransaction = async (id: number) => {
    try {
      await deleteExpenseTransaction(id);
      message.success('Xóa giao dịch thành công');
      loadTransactions();
    } catch (error) {
      message.error('Có lỗi xảy ra khi xóa giao dịch');
    }
  };

  const handleModalSuccess = () => {
    loadTransactions();
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const columns = [
    {
      title: 'Ngày',
      dataIndex: 'expense_date',
      key: 'expense_date',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      sorter: true,
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_id',
      key: 'category_id',
      render: (categoryId: number) => {
        const category = categories.find(c => c.id === categoryId);
        return category ? (
          <Tag color={category.color}>
            {category.icon} {category.name}
          </Tag>
        ) : 'N/A';
      },
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
          {formatCurrency(amount)}
        </span>
      ),
      sorter: true,
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => notes || '-',
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: ExpenseTransaction) => (
        <Space>
          <Tooltip title="Chỉnh sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditTransaction(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa giao dịch này?"
            onConfirm={() => handleDeleteTransaction(record.id)}
            okText="Có"
            cancelText="Không"
          >
            <Tooltip title="Xóa">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getCategoryById = (id: number) => {
    return categories.find(c => c.id === id);
  };

  const totalAmount = transactions.reduce((sum, transaction) => sum + transaction.amount, 0);

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Tổng chi tiêu"
              value={totalAmount}
              formatter={(value) => formatCurrency(value as number)}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Số giao dịch"
              value={total}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Lọc theo danh mục"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => handleFilterChange('category_id', value)}
            >
              {categories.map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              style={{ width: '100%' }}
              placeholder={['Từ ngày', 'Đến ngày']}
              onChange={(dates) => handleFilterChange('date_range', dates)}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Tìm kiếm..."
              prefix={<SearchOutlined />}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTransaction}
              style={{ width: '100%' }}
            >
              Thêm giao dịch
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} giao dịch`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size || 10);
            },
          }}
        />
      </Card>

      <ExpenseTransactionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        transaction={editingTransaction}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};